import Stripe from 'stripe';
import { createPaymentForOneTimePurchase, createStripePaymentIntentResponse } from './payment-intent';
import { billingCycleAnchor } from './subscription';
import type { PaymentParams } from './types/PaymentParams';

export type PaymentResult =
  | { type: 'payment_intent'; data: createStripePaymentIntentResponse }
  | { type: 'no_payment_intent'; data: Stripe.Subscription }
  | { type: 'subscription_upgrade'; data: Stripe.Subscription }
  | { type: 'setup_intent'; data: Stripe.SetupIntent };

/**
 * @deprecated Use saturn endpoints instead for creating one time purchase, creating a subscription or updating a subscription.
 */

export const createStripePaymentIntentV2 = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  freeTrialDays,
  paymentMethodTypes,
  upgradeSubscriptionId,
  subscriptionStartDateMillis,
}: PaymentParams): Promise<PaymentResult> => {
  if (upgradeSubscriptionId) {
    return await createPaymentForSubscriptionUpgrade({
      stripeInstance,
      customerId,
      price,
      coupon,
      metadata,
      freeTrialDays,
      paymentMethodTypes,
      upgradeSubscriptionId,
    });
  }

  if (price.type === 'one_time') {
    const paymentIntent = await createPaymentForOneTimePurchase({
      stripeInstance,
      customerId,
      price,
      coupon,
      metadata,
    });

    if (!paymentIntent) {
      throw new Error('Failed to create payment intent for one-time purchase');
    }

    return { type: 'payment_intent', data: paymentIntent };
  }

  return await createPaymentForSubscription({
    stripeInstance,
    customerId,
    price,
    coupon,
    metadata,
    freeTrialDays,
    paymentMethodTypes,
    subscriptionStartDateMillis,
  });
};

const createPaymentForSubscription = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  freeTrialDays,
  paymentMethodTypes,
  subscriptionStartDateMillis,
}: PaymentParams): Promise<PaymentResult> => {
  if (subscriptionStartDateMillis != null) {
    // For now, delayed subscriptions and free trials are mutually exclusive and subscription schedule takes precedence
    return await createDelayedSubscription({
      stripeInstance,
      customerId,
      price,
      coupon,
      metadata,
      subscriptionStartDateMillis,
      paymentMethodTypes,
    });
  }

  const subscription = await stripeInstance.subscriptions.create({
    customer: customerId,
    currency: price.currency,
    payment_behavior: 'default_incomplete',
    items: [{ price: price.id, quantity: 1 }],
    payment_settings: {
      save_default_payment_method: 'on_subscription',
      payment_method_types: paymentMethodTypes as Stripe.Emptyable<
        Stripe.SubscriptionCreateParams.PaymentSettings.PaymentMethodType[]
      >,
    },
    expand: ['latest_invoice.confirmation_secret', 'pending_setup_intent'],
    metadata,
    discounts: [{ coupon: coupon?.id }], // Coupon will automatically get applied to the subscription if it's valid
    trial_period_days: freeTrialDays,
    automatic_tax: { enabled: true },
  });

  const invoice = subscription.latest_invoice as Stripe.Invoice | null;

  if (!invoice || !invoice.confirmation_secret || !invoice.confirmation_secret.client_secret) {
    // If we are here it is because the subscription does not require immediate payment.
    // This can happen for free trial subscriptions - where the first payment will happen in future
    return { type: 'no_payment_intent', data: subscription };
  }

  const clientSecret = invoice.confirmation_secret.client_secret;
  const paymentIntentId = clientSecret.split('_secret')[0] as string; //if this secret is present, it will always have the format `pi_123_secret_abc`

  // If there's no payment intent (free trial active), return the subscription
  return {
    type: 'payment_intent',
    data: {
      id: paymentIntentId,
      client_secret: clientSecret,
    },
  };
};

const createPaymentForSubscriptionUpgrade = async ({
  stripeInstance,
  price,
  metadata,
  paymentMethodTypes,
  upgradeSubscriptionId,
}: Omit<PaymentParams, 'upgradeSubscriptionId'> & { upgradeSubscriptionId: string }): Promise<PaymentResult> => {
  // Retrieve the current subscription with expanded items to get current price details
  const currentUserSubscription = await stripeInstance.subscriptions.retrieve(upgradeSubscriptionId, {
    expand: ['items.data.price', 'latest_invoice'],
  });

  const currentSubscriptionItem = currentUserSubscription.items.data[0];
  if (!currentSubscriptionItem) {
    throw new Error('No subscription items found for upgrade');
  }

  const currentPrice = currentSubscriptionItem.price;
  if (!currentPrice) {
    throw new Error('Current subscription price not found');
  }

  const billing_cycle_anchor = billingCycleAnchor(currentPrice, price);

  // Update the subscription with the new price and proration behavior
  const updatedSubscription = await stripeInstance.subscriptions.update(upgradeSubscriptionId, {
    items: [
      {
        id: currentSubscriptionItem.id,
        price: price.id,
        quantity: 1,
      },
    ],
    payment_settings: {
      save_default_payment_method: 'on_subscription',
      payment_method_types: paymentMethodTypes as Stripe.Emptyable<
        Stripe.SubscriptionCreateParams.PaymentSettings.PaymentMethodType[]
      >,
    },
    // `always_invoice` charges the customer immediately for any prorated amount
    proration_behavior: 'always_invoice',
    billing_cycle_anchor,
    expand: ['latest_invoice.confirmation_secret'],
    metadata: {
      ...metadata,
      upgrade_from_price: currentPrice.id,
      upgrade_to_price: price.id,
      billing_cycle_anchor,
      upgradeProductSku: '', // remove the upgradeProductSku metadata key
    },
    automatic_tax: { enabled: true },
  });

  // Get the latest invoice from the updated subscription
  const latestInvoice = updatedSubscription.latest_invoice as Stripe.Invoice | null;

  if (!latestInvoice?.confirmation_secret || !latestInvoice.confirmation_secret.client_secret) {
    // If no invoice is created, it means no immediate payment is required
    // This can happen if the upgrade results in a credit or if the subscription is in a trial period
    return { type: 'no_payment_intent', data: updatedSubscription };
  }

  const clientSecret = latestInvoice.confirmation_secret.client_secret;
  const paymentIntentId = clientSecret.split('_secret')[0] as string; // if this secret is present, it will always have the format `pi_123_secret_abc`

  // If the payment intent is already succeeded, we don't need to return a client secret
  // The subscription upgrade was successful and no further payment is required
  const paymentIntent = await stripeInstance.paymentIntents.retrieve(paymentIntentId);
  if (paymentIntent.status === 'succeeded') {
    return { type: 'subscription_upgrade', data: updatedSubscription };
  }

  return {
    type: 'payment_intent',
    data: {
      id: paymentIntentId,
      client_secret: clientSecret,
    },
  };
};

const createDelayedSubscription = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  paymentMethodTypes,
  subscriptionStartDateMillis,
}: Omit<PaymentParams, 'subscriptionStartDateMillis'> & {
  subscriptionStartDateMillis: number;
}): Promise<PaymentResult> => {
  const schedule = await stripeInstance.subscriptionSchedules.create({
    customer: customerId,
    start_date: Math.floor(subscriptionStartDateMillis / 1000),
    end_behavior: 'release',
    default_settings: {
      collection_method: 'charge_automatically',
    },
    phases: [
      {
        items: [{ price: price.id, quantity: 1 }],
        discounts: coupon ? [{ coupon: coupon.id }] : undefined,
        proration_behavior: 'none',
        metadata: {
          ...metadata,
          phase: 'active_subscription',
        },
      },
    ],
    metadata: {
      ...metadata,
      isDelayedSubscription: 'true',
      subscriptionStartDateMillis: subscriptionStartDateMillis.toString(),
      subscriptionStartDate: new Date(subscriptionStartDateMillis).toISOString(),
    },
  });

  // For delayed subscriptions, we only need to collect payment method details
  // without creating a payment intent that could charge immediately
  const setupIntent = await stripeInstance.setupIntents.create({
    customer: customerId,
    usage: 'off_session',
    payment_method_types: paymentMethodTypes,
    metadata: {
      ...metadata,
      subscriptionScheduleId: schedule.id,
      phase: 'immediate_payment_details_for_delayed_payment',
    },
  });

  return {
    type: 'setup_intent',
    data: setupIntent,
  };
};
